import { HttpClient } from '@angular/common/http';
import { DestroyRef, inject, Injectable, OnDestroy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { BehaviorSubject, catchError, EMPTY, filter, interval, map, switchMap } from 'rxjs';


type Type = 'info' | 'warning' | 'danger';


@Injectable()
export class ReloadService implements OnDestroy {

    readonly reloadBar = new BehaviorSubject<{ type: Type; message: string; showReload: boolean } | null>(null);

    readonly timeSinceLoad = new Date().getTime();

    private destroyRef = inject(DestroyRef);

    private http = inject(HttpClient);

    initialize(version: string, frequency = 1000 * 60 * 5) {
        if (version === '@dev') {
            return;
        }

        interval(frequency).pipe(
            switchMap(_ =>
                // add time to invalidate potential cache
                this.http.get(`/version.json?t=${new Date().getTime()}`, { responseType: 'text' }).pipe(
                    map(payload => JSON.parse(payload)),
                    catchError(error => {
                        console.warn(`version check failed with error: ${error}`);
                        return EMPTY;
                    })
                )
            ),
            filter(payload => payload.revision !== version),
            map(payload => Math.round((payload.timestamp - this.timeSinceLoad) / 1000)),
            takeUntilDestroyed(this.destroyRef),
        ).subscribe(seconds => {
            const secInDay = 24 * 60 * 60;
            if (seconds < 3 * secInDay) {
                this.setReloadBar(
                    'warning',
                    'We have detected your browser has an old version of AUTOPROP loaded. Do you want to reload the page?',
                    true
                );
            } else if (seconds < 5 * secInDay) {
                this.setReloadBar(
                    'danger',
                    'We have detected your version of AUTOPROP is 3 days old or older.'
                    + ' Please reload your page to ensure full functionality.',
                    true
                );
            } else {
                this.reload();
            }
        });
    }

    setReloadBar(type: Type, message: string, showReload: boolean) {
        this.reloadBar.next({ type, message, showReload });
    }

    reload() {
        this.setReloadBar('info', 'Please wait, your browser is loading the latest version of AUTOPROP...', false);
        (window.location.reload as any)(true);
    }

    ngOnDestroy() {
        this.reloadBar.complete();
    }
}
